import { Lambda } from "src/app/support/lambda";
import IdleJs from "idle-js";
import { ApplicationRef } from "@angular/core";
import { sntLog } from "src/app/common/logger";

export type IdlePermissionStatus = "granted" | "denied" | "prompt";

const logger = sntLog("IdleDetector");

interface IdleDetector {
  addEventListener(
    type: "change",
    listener: (
      this: IdleDetector,
      ev: { userState: "active" | "idle"; screenState: "locked" | "unlocked" }
    ) => unknown,
    options?: boolean | AddEventListenerOptions
  ): void;
  start(options: { threshold: number; signal: AbortSignal }): Promise<void>;
  screenState: "locked" | "unlocked";
  userState: "active" | "idle";
}

declare const IdleDetector: {
  new (): IdleDetector;
  requestPermission(): Promise<"granted" | "denied">;
};

export interface IdleTimer {
  activate(
    onIdleTimeout: Lambda<void>,
    timeout: number,
    onTick: (remainingTime: number) => void
  ): Promise<boolean>;
  deactivate(): Promise<boolean>;
  isPermissionGranted(): Promise<boolean>;
  requestPermission(): Promise<boolean>;
  permissionStatus(): Promise<IdlePermissionStatus>;
}

export class WebIdleTimer implements IdleTimer {
  private idleJsRef: IdleJs | undefined;

  constructor(private applicationRef: ApplicationRef) {}

  async activate(
    onIdleTimeout: Lambda<void>,
    timeout: number,
    onTick: (remainingTime: number) => void
  ): Promise<boolean> {
    this.applicationRef.whenStable().then(() => {
      this.idleJsRef = new IdleJs({
        idle: timeout,
        onIdle: () => {
          onIdleTimeout();
        }
      });

      this.idleJsRef.start();
    });

    return true;
  }

  async deactivate(): Promise<boolean> {
    this.idleJsRef?.stop().reset();
    return true;
  }
  async isPermissionGranted(): Promise<boolean> {
    return true;
  }

  async requestPermission(): Promise<boolean> {
    return true;
  }

  async permissionStatus(): Promise<IdlePermissionStatus> {
    return "granted";
  }
}

export class NativeIdleTimer implements IdleTimer {
  private idleDetector: IdleDetector | undefined;
  private idleDetectorController: AbortController | undefined;

  #initialized: boolean = false;

  async activate(
    onIdleTimeout: Lambda<void>,
    timeout: number
  ): Promise<boolean> {
    if (!(await this.isPermissionGranted())) {
      if ((await IdleDetector.requestPermission()) !== "granted") {
        logger.error("permission denied.");
        return false;
      }
    }

    this.idleDetector = new IdleDetector();
    this.idleDetectorController = new AbortController();
    this.idleDetector.addEventListener("change", (idleEvent) => {
      logger.debug(
        `screen: "${this.idleDetector?.screenState}" - user: "${this.idleDetector?.userState}"`
      );

      if (!this.#initialized) {
        logger.debug("initialized");
      }

      if (
        this.#initialized &&
        (this.idleDetector?.screenState === "locked" ||
          this.idleDetector?.userState === "idle")
      ) {
        logger.debug("onIdleTimeout()");
        onIdleTimeout();
      }

      this.#initialized = true;
    });

    this.idleDetector.start({
      threshold: timeout,
      signal: this.idleDetectorController.signal
    });

    return true;
  }

  async deactivate(): Promise<boolean> {
    this.idleDetectorController?.abort();

    return true;
  }

  async isPermissionGranted(): Promise<boolean> {
    try {
      const result = await navigator.permissions.query({
        name: <any>"idle-detection"
      });

      return result.state === "granted";
    } catch (ex) {
      logger.error(ex);
      return false;
    }
  }

  async requestPermission(): Promise<boolean> {
    return (await IdleDetector.requestPermission()) === "granted";
  }

  async permissionStatus(): Promise<IdlePermissionStatus> {
    try {
      const result = await navigator.permissions.query({
        name: <any>"idle-detection"
      });

      return result.state;
    } catch (ex) {
      logger.error(ex);
      return "denied";
    }
  }
}

export async function idleTimerFactory(
  applicationRef: ApplicationRef
): Promise<IdleTimer> {
  if (typeof IdleDetector === "undefined") {
    return new WebIdleTimer(applicationRef);
  }

  try {
    const result = await navigator.permissions.query({
      name: <any>"idle-detection"
    });

    switch (result.state) {
      case "denied": {
        return new WebIdleTimer(applicationRef);
      }
      case "prompt":
      case "granted": {
        return new NativeIdleTimer();
      }
    }
  } catch (ex) {
    logger.error(ex);
    return new WebIdleTimer(applicationRef);
  }
}
